import React, { useEffect, useRef, useState } from 'react';
import './AnimatedLogo.css';

const AnimatedLogo = ({ width = 200, height = 260 }) => {
    const pathRef = useRef(null);
    const [spiralPoints, setSpiralPoints] = useState([]);

    useEffect(() => {
        const path = pathRef.current;
        if (!path) return;

        const pathLength = path.getTotalLength();
        let animationId;
        let progress = 0;
        const speed = 0.001;

        // Number of rings in the spiral
        const numRings = 12;
        // Spacing between rings along the path
        const ringSpacing = 0.015;

        const animate = () => {
            progress += speed;
            if (progress > 1) progress = 0;

            const points = [];

            for (let i = 0; i < numRings; i++) {
                // Each ring is slightly behind the previous one along the path
                let ringProgress = progress - (i * ringSpacing);
                if (ringProgress < 0) ringProgress += 1;
                if (ringProgress > 1) ringProgress -= 1;

                // Get point along the path
                const pointOnPath = path.getPointAtLength(ringProgress * pathLength);

                // Calculate the spiral rotation angle for this ring
                // As progress moves, the spiral rotates around the path
                const spiralAngle = (progress * Math.PI * 8) + (i * Math.PI / 3);

                // Calculate perpendicular offset to create 3D spiral effect
                // Get tangent direction
                const nextPoint = path.getPointAtLength(Math.min((ringProgress + 0.002) * pathLength, pathLength));
                const dx = nextPoint.x - pointOnPath.x;
                const dy = nextPoint.y - pointOnPath.y;
                const len = Math.sqrt(dx * dx + dy * dy) || 1;

                // Perpendicular vector (normalized)
                const perpX = -dy / len;
                const perpY = dx / len;

                // Spiral radius and offset
                const spiralRadius = 15;
                const offsetX = Math.cos(spiralAngle) * spiralRadius * perpX;
                const offsetY = Math.cos(spiralAngle) * spiralRadius * perpY;

                // Z-depth effect (affects size and opacity)
                const zDepth = Math.sin(spiralAngle);

                points.push({
                    x: pointOnPath.x + offsetX,
                    y: pointOnPath.y + offsetY,
                    z: zDepth,
                    opacity: 0.3 + (zDepth + 1) * 0.35, // 0.3 to 1.0
                    radius: 3 + (zDepth + 1) * 3, // 3 to 9
                    index: i
                });
            }

            setSpiralPoints(points);
            animationId = requestAnimationFrame(animate);
        };

        animate();

        return () => {
            if (animationId) cancelAnimationFrame(animationId);
        };
    }, []);

    // Sort by z-depth for proper layering (back to front)
    const sortedPoints = [...spiralPoints].sort((a, b) => a.z - b.z);

    return (
        <svg
            width={width}
            height={height}
            viewBox="0 0 980 1236"
            className="animated-logo"
        >
            <defs>
                {/* Glow filter */}
                <filter id="spiralGlow" x="-100%" y="-100%" width="300%" height="300%">
                    <feGaussianBlur stdDeviation="3" result="blur" />
                    <feMerge>
                        <feMergeNode in="blur" />
                        <feMergeNode in="SourceGraphic" />
                    </feMerge>
                </filter>
            </defs>

            <g transform="matrix(1,0,0,1,-416.892,-551.297)">
                <g transform="matrix(0.866171,0.499747,-0.499747,0.866171,691.155,-308.202)">
                    {/* Main logo path - stroke only */}
                    <path
                        ref={pathRef}
                        className="logo-path"
                        d="M786.044,1505.04C786.044,1505.04 809.524,1533.11 822.442,1546.2C836.005,1559.95 867.424,1587.53 867.424,1587.53C867.424,1587.53 872.048,1558.5 873.484,1545.11C874.739,1533.41 876.037,1507.2 876.037,1507.2L786.044,1505.04ZM975.905,1071.91C975.905,1071.91 927.457,694.469 1007.13,673.963C1092.41,652.013 1106.18,747.062 1107.3,761.539C1110.41,801.67 1104.52,953.484 1090.51,1004.3C1048.91,1155.05 975.905,1071.91 975.905,1071.91ZM984.91,1195.44C984.91,1195.44 1023.11,1212.91 1042.43,1211.46C1201.63,1199.52 1217.75,926.742 1225.09,834.375C1232.26,744.217 1215.26,671.59 1154.2,600.114C1112.36,551.126 1027.34,543.076 960.332,567.203C878.629,596.621 854.4,689.432 847.826,755.537C841.005,824.134 853.258,1000.22 853.258,1000.22C853.258,1000.22 762.717,926.981 739.849,886.885C712.738,839.351 693.666,723.548 693.666,723.548C693.666,723.548 707.154,667.025 643.484,640.867C619.8,631.136 577.831,647.385 565.949,691.256C554.232,734.519 546.754,836.511 543.931,911.861C541.024,989.463 537.338,1130.12 550.702,1177.8C556.576,1198.76 592.058,1226.55 612.473,1229.16C656.135,1234.75 667.071,1214.9 677.33,1198.99C686.08,1185.42 687.689,1164.2 681.903,1150.11C675.293,1134.01 662.708,1127 662.708,1127L660.443,976.427C660.443,976.427 710.287,1037.4 742.926,1063.21C776.988,1090.14 862.501,1142.47 862.501,1142.47L878.034,1389.12C878.034,1389.12 762.159,1388.18 722.049,1379.43C690.927,1372.64 632.347,1338.19 632.347,1338.19C632.347,1338.19 589.115,1287.51 529.972,1267.82C505.18,1259.57 474.792,1268.2 462.769,1292.75C440.141,1338.96 472.924,1367.71 493.903,1382.93C522.091,1403.39 573.87,1438.02 573.87,1438.02C573.87,1438.02 630.12,1496.7 675.258,1559.97C717.611,1619.34 840.46,1725.8 889.473,1718.29C1001.78,1701.08 993.665,1502.44 993.665,1502.44C993.665,1502.44 1093.66,1505.25 1138.14,1516.45C1198.01,1531.52 1271.85,1567.39 1271.85,1567.39C1271.85,1567.39 1266.35,1600.94 1272.93,1612.5C1285.74,1635 1321.32,1644.8 1340.58,1641.62C1376.31,1635.73 1382.49,1595.17 1386.43,1571.29C1390.37,1547.4 1370.56,1501.9 1339.06,1476.08C1305.98,1448.97 1229.18,1418.63 1178.09,1406.11C1122.06,1392.37 993.082,1389.06 993.082,1389.06L984.91,1195.44Z"
                    />

                    {/* 3D Spiral - multiple circles with varying z-depth */}
                    <g filter="url(#spiralGlow)">
                        {sortedPoints.map((point, idx) => (
                            <circle
                                key={idx}
                                cx={point.x}
                                cy={point.y}
                                r={point.radius}
                                fill="white"
                                opacity={point.opacity}
                            />
                        ))}
                    </g>
                </g>
            </g>
        </svg>
    );
};

export default AnimatedLogo;
